import { Button, Icon, Modal, Toggle } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@hero/components/atoms';
import { DetailItem, ProductVerticalList } from '@hero/components/molecules';
import { configs } from '@hero/configs';
import { PRODUCT_TYPES } from '@hero/constants';
import { useGetCharity } from '@hero/hooks';
import { useGetUser } from '@hero/services/organization-service/hooks';

import { AvailableCharitiesModal } from '../AvailableCharitiesModal';
import { DonateToCharityModal } from '../DonateToCharityModal';
import { ThankYouEmailsModal } from '../ThankYouEmailsModal';
import { UnwrapModal } from '../UnwrapModal';

import * as S from './style';

function AdvancedSettingsModal({
  isLoading,
  isOpen,
  onRequestClose,
  settings,
  onSettings,
  charities
}) {
  const { t } = useTranslation();

  // const [showGiftListInfoModal, setShowGiftListInfoModal] = useState(false);
  const [showUnwrapModal, setShowUnwrapModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showCharityInfoModal, setShowCharityInfoModal] = useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = useState(false);
  const { data: userData } = useGetUser();
  const { anyCharityItemExists } = useGetCharity();

  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings({
      ...settings,
      charityRules: {
        ...settings?.charityRules,
        enabled: anyCharityItemExists(charities),
        selectedCharities: charities
      }
    });
  }, [charities]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onRequestClose(false)}
      title={t('advancedSettings')}
      primaryButtonProps={{
        children: t('saveChanges'),
        onClick: () => {
          onSettings(localSettings, localSettings?.charityRules?.selectedCharities);
          onRequestClose(false);
        }
      }}
      secondaryButtonProps={{
        children: t('cancel'),
        onClick: () => onRequestClose(false)
      }}
      size="lg"
    >
      <S.AdvancedSettingsContainer>
        <DetailItem title={`${t('itemReGifting')}:`} className="item-d-flex">
          {t('itemReGiftDesc')}
          <Toggle
            checked={!localSettings?.disableReGifting}
            disabled={isLoading}
            onChange={async () => {
              setLocalSettings(prev => ({
                ...prev,
                disableReGifting: !prev?.disableReGifting
              }));
            }}
          />
        </DetailItem>
        {configs.advanceSettings.premiumDates !== false && (
          <DetailItem title={`${t('premiumDates')}:`} className="item-d-flex">
            {t('premiumDatesDesc')}
            <Toggle
              checked={!!localSettings?.allowPremiumDates}
              disabled={isLoading}
              onChange={async () => {
                setLocalSettings(prev => ({
                  ...prev,
                  allowPremiumDates: !prev?.allowPremiumDates
                }));
              }}
            />
          </DetailItem>
        )}
        {configs.advanceSettings.scratchEffectEnabled && (
          <DetailItem
            title={`${t('unwrapEffect')}:`}
            className="item-d-flex"
            titleIcon
            titleIconOnClick={() => setShowUnwrapModal(true)}
          >
            {t('advanceSettingsDetailItem1Desc')}
            <Toggle
              checked={!localSettings?.disableScratchEffect}
              disabled={isLoading}
              onChange={async () => {
                setLocalSettings(prev => ({
                  ...prev,
                  disableScratchEffect: !prev?.disableScratchEffect
                }));
              }}
            />
            <UnwrapModal isOpen={showUnwrapModal} setIsOpen={setShowUnwrapModal} />
          </DetailItem>
        )}
        <DetailItem
          title={`${t('thankYouEmails')}`}
          className="item-d-flex"
          titleIcon
          titleIconOnClick={() => setShowThankYouModal(true)}
        >
          {t('thankYouEmailsDescription')}
          <Toggle
            checked={localSettings?.receiveThankYouNotes}
            disabled={isLoading}
            onChange={async () => {
              setLocalSettings(prev => ({
                ...prev,
                receiveThankYouNotes: !prev?.receiveThankYouNotes
              }));
            }}
          />
          <ThankYouEmailsModal
            isOpen={showThankYouModal}
            onRequestClose={() => {
              setShowThankYouModal(false);
            }}
            senderName={userData?.firstName || t('sender')}
          />
        </DetailItem>
        <DetailItem
          title={`${t('donateToCharity')}:`}
          className="item-d-flex"
          titleIcon
          titleIconOnClick={() => setShowCharityInfoModal(true)}
          extraField={() =>
            localSettings?.charityRules?.enabled ? (
              <Card style={{ gap: 8 }}>
                <DetailItem
                  title={t('charities')}
                  editable={true}
                  editButton={() => (
                    <Button
                      startIcon={<Icon name="edit" />}
                      variant="link"
                      onClick={() => setShowSelectCharitiesModal(true)}
                    >
                      {t('edit')}
                    </Button>
                  )}
                >
                  {localSettings?.charityRules?.selectedCharities?.length}
                </DetailItem>
                {localSettings?.charityRules?.selectedCharities.length ? (
                  <ProductVerticalList
                    isCharityList
                    products={{ docs: localSettings?.charityRules?.selectedCharities }}
                    creationProducts={localSettings?.charityRules?.selectedCharities.filter(
                      p => p.meta?.productType === PRODUCT_TYPES.CHARITY
                    )}
                    selectedProducts={localSettings?.charityRules?.selectedCharities}
                    onRemoveProduct={id => {
                      setLocalSettings(e => ({
                        ...e,
                        charityRules: {
                          enabled: e.charityRules?.enabled,
                          selectedCharities: localSettings?.charityRules?.selectedCharities.filter(
                            item => item.id !== id
                          )
                        }
                      }));
                    }}
                    // updateProducts={updateCharities}
                  />
                ) : (
                  <Button onClick={() => setShowSelectCharitiesModal(true)}>
                    {t('addACharitableGiving')}
                  </Button>
                )}
              </Card>
            ) : null
          }
        >
          {t('donateToCharityToggleDesc')}
          <Toggle
            checked={localSettings?.charityRules?.enabled}
            disabled={isLoading}
            onChange={async e => {
              setLocalSettings(prev => ({
                ...prev,
                charityRules: {
                  enabled: e.target.checked,
                  selectedCharities: []
                }
              }));
            }}
          />
          {showCharityInfoModal && (
            <DonateToCharityModal
              isOpen={showCharityInfoModal}
              setIsOpen={setShowCharityInfoModal}
              primaryButtonProps={{
                onClick: () => {
                  setShowCharityInfoModal(false);
                  setShowSelectCharitiesModal(true);
                },
                children: t('seeAvailableCharities')
              }}
              secondaryButtonProps={{
                onClick: () => setShowCharityInfoModal(false),
                children: t('cancel')
              }}
              descriptionTextKey="donateToCharityDesc3"
            />
          )}
          {showSelectCharitiesModal && (
            <AvailableCharitiesModal
              isOpen={showSelectCharitiesModal}
              setIsOpen={setShowSelectCharitiesModal}
              selectedCharities={localSettings?.charityRules?.selectedCharities}
              setSelectedCharities={selectedCharities =>
                setLocalSettings({
                  ...localSettings,
                  charityRules: {
                    enabled: true,
                    selectedCharities
                  }
                })
              }
            />
          )}
        </DetailItem>
        {/* {configs.advanceSettings.onlyCollectAddressEnabled && (
          <DetailItem
            title={t('advanceSettingsDetailItem3Title')}
            titleIcon
            className="item-d-flex"
            titleIconOnClick={() => setShowGiftListInfoModal(true)}
          >
            {t('advanceSettingsDetailItem3Desc')}
            <Toggle
              checked={onlyCollectAddress}
              disabled={disabled}
              onChange={() => {
                setAdvanceSettings({
                  settings: {
                    ...localSettings?.settings,
                    onlyCollectAddress: !onlyCollectAddress
                  }
                });
              }}
            />
            <GiftListModal isOpen={showGiftListInfoModal} setIsOpen={setShowGiftListInfoModal} />
          </DetailItem>
        )} */}
      </S.AdvancedSettingsContainer>
    </Modal>
  );
}

AdvancedSettingsModal.propTypes = {
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    onlyCollectAddress: PropTypes.bool,
    disableScratchEffect: PropTypes.bool,
    receiveThankYouNotes: PropTypes.bool,
    disableReGifting: PropTypes.bool,
    allowPremiumDates: PropTypes.bool,
    charityRules: PropTypes.shape({
      enabled: PropTypes.bool,
      selectedCharities: PropTypes.arrayOf(PropTypes.string)
    })
  }),
  onSettings: PropTypes.func.isRequired
};

export { AdvancedSettingsModal };
